/* eslint-disable */
// NewItemModal — Originals "New Item" creation modal, rebuilt in New DNA
// (Create-Modals DNA arc, Phase B). Orchestrator: owns the brand→SKU + material
// cascade handlers + required-6 client-side gating, then calls the unchanged
// handleAdd write path (useItemHandlers → items insert; ensureUniqueSku /
// generateUniqueSku / frameCategory-only-if-set all preserved). Form state lives
// in the parent (App.js) — this modal only reads form + writes via setForm.
import React, { useState, useRef, useMemo } from "react";
import { generateUniqueSku } from "../../utils";
import DnaModalShell from "../dna/DnaModalShell";
import MissingFieldsPopup from "../dna/MissingFieldsPopup";
import NewItemFields from "./NewItemFields";

// Decision 1 — required-6 hardcoded client-side (handleAdd still enforces Brand).
const REQUIRED = [
  { key: "brand", label: "Brand" },
  { key: "frameCategory", label: "Optic / Sunglasses" },
  { key: "material", label: "Material" },
  { key: "shape1", label: "Shape 1" },
  { key: "status", label: "Status" },
  { key: "receivedDate", label: "Received Date" },
];

export function NewItemModal({ setShowAdd, form, setForm, fields, handleAdd, saving, imageUploading, setImageUploading, myProfile, role, isMobile }) {
  const [errs, setErrs] = useState({});
  const [missingModal, setMissingModal] = useState(null);
  const [skuLoading, setSkuLoading] = useState(false);
  // Dirty flag for the DnaModalShell unsaved-changes guard — marked by `set`
  // (every user field change routes through it; no programmatic sets here).
  const [touched, setTouched] = useState(false);
  const fieldRefs = useRef({});
  const regField = (key) => (el) => { fieldRefs.current[key] = el; };

  const canAddBrands = myProfile?.can_add_brands || role === "admin";
  const getOpts = (key) => (fields.find((f) => f.key === key) || { options: [] }).options;
  const brandOpts = useMemo(() => {
    const o = (fields.find((f) => f.key === "brand")?.options || []).slice()
      .filter((x) => (x === "Other" ? canAddBrands : true)).sort((a, b) => a.localeCompare(b));
    const idx = o.indexOf("Other");
    if (idx > 0) { o.splice(idx, 1); o.unshift("Other"); }
    return o;
  }, [fields, canAddBrands]);

  const set = (k, v) => { setTouched(true); setForm((p) => ({ ...p, [k]: v })); if (errs[k]) setErrs((p) => ({ ...p, [k]: false })); };

  // Async SKU gen (DB-unique), mirrors FrameForm.updateSkuForBrand verbatim.
  const updateSkuForBrand = async (brandName) => {
    if (!brandName || brandName === "Other") { set("sku", ""); return; }
    setSkuLoading(true);
    try { set("sku", await generateUniqueSku(brandName)); }
    catch (e) { console.error("SKU generation failed:", e); }
    setSkuLoading(false);
  };
  const onBrandChange = (v) => { set("brand", v); set("brandCustom", ""); updateSkuForBrand(v); };
  const onBrandCustomChange = (v) => { set("brandCustom", v); updateSkuForBrand(v); };
  const onMaterialChange = (v) => { set("material", v); set("materialDetail", ""); };

  const finalBrand = (form.brand === "Other" && form.brandCustom) ? form.brandCustom : form.brand;
  const isFilled = (key) => (key === "brand" ? !!finalBrand : !!(form[key] && String(form[key]).trim()));
  const requiredMet = REQUIRED.filter((r) => isFilled(r.key)).length;

  const onCreate = () => {
    const newErrs = {}; const missing = [];
    REQUIRED.forEach((r) => {
      if (!isFilled(r.key)) {
        newErrs[r.key] = true;
        missing.push({ label: r.label, onJump: () => fieldRefs.current[r.key]?.scrollIntoView({ behavior: "smooth", block: "center" }) });
      }
    });
    if (missing.length) { setErrs(newErrs); setMissingModal(missing); return; }
    setErrs({});
    handleAdd();
  };

  return (
    <DnaModalShell
      open
      onClose={() => setShowAdd(false)}
      title="New Item"
      subtitle={`New original frame · ${finalBrand || "No brand"}`}
      requiredMet={requiredMet}
      requiredTotal={REQUIRED.length}
      onSubmit={onCreate}
      submitLabel="Create Item"
      submitting={saving || imageUploading}
      submitDisabled={saving || imageUploading}
      isMobile={isMobile}
      isDirty={touched}
      data-test="new-item-modal"
      overlay={missingModal && <MissingFieldsPopup fields={missingModal} onClose={() => setMissingModal(null)} />}
    >
      <NewItemFields
        form={form} set={set} errs={errs} regField={regField} getOpts={getOpts}
        brandOpts={brandOpts} onBrandChange={onBrandChange} onBrandCustomChange={onBrandCustomChange}
        onMaterialChange={onMaterialChange} skuLoading={skuLoading} onUploadingChange={setImageUploading}
      />
    </DnaModalShell>
  );
}
